import React, { useState } from "react";
import { motion } from "framer-motion";
import { FaWhatsapp } from "react-icons/fa";
import { Leaf, Check } from "lucide-react";
import { MENU, waOrder } from "../../data/zingly";

const Card = ({ item, index, onOrder }) => {
  const [flipped, setFlipped] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.7, delay: index * 0.15 }}
      className="relative"
      data-testid={`menu-card-${item.id}`}
    >
      <div
        className={`flip-card ${flipped ? "is-flipped" : ""} h-[460px] rounded-3xl`}
        onClick={() => setFlipped((v) => !v)}
      >
        <div className="flip-card-inner rounded-3xl">
          {/* Front */}
          <div className="flip-card-front bg-white border border-orange-100 shadow-[0_20px_50px_-20px_rgba(245,130,32,0.25)]">
            {item.badge && <div className="ribbon">{item.badge}</div>}
            <div className="relative h-64 overflow-hidden">
              <img
                src={item.image}
                alt={item.title}
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-[11px] font-semibold text-[#2E7D32]">
                <Leaf className="w-3 h-3" /> Pure Veg
              </div>
            </div>
            <div className="p-6">
              <h3 className="font-serif text-2xl font-extrabold text-[#1E1E1E]">{item.title}</h3>
              <p className="mt-2 text-sm text-[#555555] leading-relaxed line-clamp-2">{item.description}</p>
              <div className="mt-5 flex items-center justify-between">
                <div className="font-serif text-3xl font-extrabold text-[#F58220]">₹{item.price}</div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onOrder(item);
                  }}
                  data-testid={`order-btn-${item.id}`}
                  className="btn-3d inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#F58220] to-[#E16608] text-white font-semibold text-sm"
                >
                  Order
                </button>
              </div>
              <div className="mt-4 text-[11px] uppercase tracking-wider text-[#555555]/70 text-center">
                Tap card to view details
              </div>
            </div>
          </div>

          {/* Back */}
          <div className="flip-card-back bg-gradient-to-br from-[#F58220] to-[#E16608] text-white p-7 flex flex-col">
            <h3 className="font-serif text-2xl font-extrabold">{item.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-white/90">{item.longDescription}</p>
            <div className="mt-5 space-y-2.5">
              {item.ingredients.map((ing) => (
                <div key={ing} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={3} />
                  <span>{ing}</span>
                </div>
              ))}
            </div>
            <div className="mt-auto pt-4 flex items-center justify-between">
              <div className="font-serif text-3xl font-extrabold">₹{item.price}</div>
              <a
                href={waOrder(item.title)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                data-testid={`order-back-btn-${item.id}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#F58220] font-semibold text-sm shadow-lg hover:scale-105 transition"
              >
                <FaWhatsapp className="w-4 h-4" /> Order Now
              </a>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const MenuSection = ({ onOrder }) => {
  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="relative py-24 md:py-32 bg-white overflow-hidden"
    >
      <div className="absolute top-20 right-10 w-72 h-72 rounded-full bg-[#F58220]/5 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 md:px-10 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-block text-xs uppercase tracking-[0.3em] text-[#F58220] font-semibold mb-3">
            Our Menu
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#1E1E1E] heading-accent">
            Our Signature Menu
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#555555] max-w-2xl mx-auto">
            Simple. Fresh. Delicious. — Made every morning with hand-fermented batter and
            traditional South Indian techniques.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {MENU.map((item, i) => (
            <Card key={item.id} item={item} index={i} onOrder={onOrder} />
          ))}
        </div>
      </div>
    </section>
  );
};
